import { z } from 'zod';

import { entityIdSchema, isoDateTimeSchema } from '../primitives/schema';

/**
 * Organisation settings — the school's own structure.
 *
 * Deliberately **not** part of `fundPolicy`, even though both are edited from a
 * settings screen. The fund policy is versioned per policy year: coverage
 * tiers, declaration items, and dependent rules are expected to differ between
 * 2026-2027 and 2027-2028. Branches are not — the school's nine branches
 * persist across years, and folding them into the policy would mean copying the
 * branch list forward every year and creating a second place a branch could be
 * defined.
 *
 * Written only by `committee_manager`; read by anyone with an employee record,
 * because the registration screen offers the branch list.
 */

export const branchSchema = z.object({
  id: entityIdSchema,
  nameAr: z.string().trim().min(2, 'اسم الفرع مطلوب').max(120),
  /**
   * Retired branches stay listed so that historical employee records remain
   * readable; they are simply not offered when registering.
   */
  active: z.boolean(),
  order: z.number().int().min(0).max(999),
});

export const organisationSettingsSchema = z
  .object({
    /** §6.2 — persisted schemas carry a version. */
    schemaVersion: z.literal(1),

    branches: z
      .array(branchSchema)
      .min(1, 'يجب تعريف فرع واحد على الأقل')
      // Bounded: the list is read on the registration screen, and an unbounded
      // array is a cost defect as much as a usability one (§11.5.1).
      .max(50, 'عدد الفروع أكبر من المسموح')
      .refine(
        (branches) => new Set(branches.map((branch) => branch.id)).size === branches.length,
        'معرّفات الفروع مكررة',
      )
      .refine(
        (branches) => branches.some((branch) => branch.active),
        'يجب تفعيل فرع واحد على الأقل',
      ),

    updatedAt: isoDateTimeSchema,
    /** From the verified server identity, never the client (§4.2). */
    updatedBy: entityIdSchema,
  })
  .strict();

export function activeBranches(settings: OrganisationSettings): readonly Branch[] {
  return settings.branches
    .filter((branch) => branch.active)
    .sort((a, b) => a.order - b.order);
}

export function findBranch(
  settings: OrganisationSettings,
  branchId: string,
): Branch | undefined {
  return settings.branches.find((branch) => branch.id === branchId);
}

export type Branch = z.infer<typeof branchSchema>;
export type OrganisationSettings = z.infer<typeof organisationSettingsSchema>;
